import { batch, effect } from "@preact/signals";
import store from "@lib/store";
import { dispatchAnalyticsEvent } from "../../analytics/discount";
import type { MedusaProduct } from "src/types";
import type { DiscountCode } from "@lib/store/types/discount";
import { Coupon } from "./coupon";
import { couponManager, type AddCouponResult } from "./couponManager";

const persistStoragePromocodeKey = "promocodeConditions";

export const PROMOCODE_ADD_ERROR_CODES = {
  ALREADY_APPLIED: "already-applied",
  NOT_WITH: "not-with",
  REACH_LIMIT: "reach-limit",
  INVALID: "invalid",
} as const;

export type PromocodeAddError =
  (typeof PROMOCODE_ADD_ERROR_CODES)[keyof typeof PROMOCODE_ADD_ERROR_CODES];

// валидация что купон норм от бэка
const getDiscountCodeFromServer = async (
  code: string,
): Promise<Coupon | null> => {
  try {
    const couponResponse = await store.getDiscountCode(code);
    if ("error" in couponResponse) {
      return null;
    }
    return new Coupon(
      couponResponse.code,
      couponResponse.discount,
      couponResponse.tags,
    );
  } catch {
    return null;
  }
};

type CouponSourceGetter = () => string[] | null;

// достанем один купон из урла (потом легко переделать на два)
const getCouponsNamesFromUrlParams: CouponSourceGetter = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const searchParamCoupon = searchParams.getAll("promocode");
  if (searchParamCoupon) {
    return searchParamCoupon;
  }
  return null;
};

//  обновим купоны в сторе
const updateCouponstInPersistStorage = (coupons: string[]) => {
  localStorage.setItem(persistStoragePromocodeKey, JSON.stringify(coupons));
};

// может достать 0-2 купона, (один экстра, один обычный)
const getCouponsFromPersistStorage: CouponSourceGetter = () => {
  const coupons = localStorage.getItem(persistStoragePromocodeKey);
  try {
    if (coupons) {
      return JSON.parse(coupons);
    }
    return null;
  } catch {
    return null;
  }
};

const isNewFormat = (
  data: DiscountCode | DiscountCode[] | string[],
): data is string[] => Array.isArray(data) && typeof data[0] === "string";

const mapOldPersistStorageFormat = (): void => {
  const discountStorageValue = localStorage.getItem(persistStoragePromocodeKey);
  if (discountStorageValue) {
    const oldData: DiscountCode | DiscountCode[] | string[] =
      JSON.parse(discountStorageValue);
    const isArray = Array.isArray(oldData);
    // new format - do not need mapping
    if (isNewFormat(oldData)) return;

    // Nastya`s format - Array of DiscountCode
    if (isArray) {
      updateCouponstInPersistStorage(oldData.map((el) => el.code));
    }

    // Pasha`s format - Object of DiscountCode
    else {
      updateCouponstInPersistStorage([oldData.code]);
    }
  }
};

const filterCoupons = (
  couponSettled: PromiseSettledResult<Coupon | null>,
): couponSettled is PromiseFulfilledResult<Coupon> =>
  couponSettled.status === "fulfilled" && couponSettled.value !== null;

// если из урла что-то достали, то провалидируем на сервере и положим в локалстор / заменим текущие (если они есть)
export const initDiscounts = async () => {
  mapOldPersistStorageFormat();
  const couponsFromUrl = new Set(getCouponsNamesFromUrlParams() ?? []);
  const couponsFromStorage = getCouponsFromPersistStorage() ?? [];
  const couponsNamesSet = new Set([...couponsFromUrl, ...couponsFromStorage]);
  const coupons = (
    await Promise.allSettled(
      Array.from(couponsNamesSet).map(getDiscountCodeFromServer),
    )
  ).filter(filterCoupons);

  // hack for breaking the loop when we can't add more coupons
  batch(() =>
    coupons.some((coupon) => {
      return (
        couponManager.addCoupon(coupon.value).error ===
        PROMOCODE_ADD_ERROR_CODES.REACH_LIMIT
      );
    }),
  );

  couponManager.coupons.value.forEach((coupon) => {
    if (couponsFromUrl.has(coupon.code)) {
      dispatchAnalyticsEvent(coupon.code, "url");
    }
  });

  effect(() => {
    updateCouponstInPersistStorage(
      couponManager.coupons.value.map((coupon) => coupon.code),
    );
  });
};

export const validateAndApplyCoupon = async (
  code: string,
): Promise<AddCouponResult> => {
  const coupon = await getDiscountCodeFromServer(code);

  if (coupon === null) {
    return {
      isSuccess: false,
      error: PROMOCODE_ADD_ERROR_CODES.INVALID,
    };
  }

  return couponManager.addCoupon(coupon);
};

export const isCouponApplicableForProduct = (
  coupon: Coupon,
  product: MedusaProduct | null,
) => product?.tags?.some((tag) => coupon.tags.includes(tag.value)) ?? false;
